import { IFsLprProviderInterface } from '../../../../../api';
import { ViLPRProvider } from '../../../../../shared/models';

export const AXON_VENDOR_NAME = 'axon';

const CAMERA_MODEL_SELECTION_KEY_AXON_VI_AXON = 'axon_vi_axon';

const CAMERA_MODEL_SELECTION_KEY_AXON_VI_WWA = 'axon_vi_wwa';

/**
 * Creates unique keys to track Axon and WWA selections separately
 * Both use the same provider but appear as separate source options to users
 */
export function getCameraModelSelectionKey(
  providerName: ViLPRProvider,
  cameraVendor?: string,
): string {
  if (providerName === ViLPRProvider.AXON_VI) {
    return cameraVendor === AXON_VENDOR_NAME
      ? CAMERA_MODEL_SELECTION_KEY_AXON_VI_AXON
      : CAMERA_MODEL_SELECTION_KEY_AXON_VI_WWA;
  }

  return providerName;
}

/**
 * Checks if the providers array contains an Axon VI provider.
 *
 * @param providers - Array of LPR providers
 * @returns True if Axon VI provider is present, false otherwise
 */
export function hasAxonVI(providers: IFsLprProviderInterface[]): boolean {
  return providers.some((provider) => provider.nameEnum === ViLPRProvider.AXON_VI);
}

/**
 * Tracks which providers have errors or warnings
 * Used to grey out sources in the dropdown and adjust result counts
 */
export function buildProviderIssueState(
  providers: IFsLprProviderInterface[],
): Record<string, { hasWarnings: boolean; hasErrors: boolean }> {
  const providerIssueState: Record<string, { hasWarnings: boolean; hasErrors: boolean }> = {};

  providers.forEach((provider) => {
    const providerKey = provider.nameEnum ?? (provider.name as ViLPRProvider);
    providerIssueState[providerKey] = {
      hasWarnings: (provider.warnings?.length ?? 0) > 0,
      hasErrors: (provider.errors?.length ?? 0) > 0,
    };
  });

  return providerIssueState;
}

/**
 * Determines if a source should be excluded from the "X sources selected" count
 * Skips sources with errors, or sources with warnings when partial matches are hidden
 */
export function shouldSkipProvider(
  providerName: ViLPRProvider,
  providerIssueState: Record<string, { hasWarnings: boolean; hasErrors: boolean }>,
  shouldShowPartialMatches: boolean,
): boolean {
  const issueState = providerIssueState[providerName];

  const isWWA = providerName === ViLPRProvider.WORKS_WITH_AXON;
  if (isWWA) {
    return false;
  }

  if (!issueState) {
    return true;
  }

  if (issueState.hasErrors) {
    return true;
  }

  if (issueState.hasWarnings && !shouldShowPartialMatches) {
    return true;
  }

  return false;
}
